import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardMedia,
  IconButton,
  Stack,
  Paper,
  Snackbar,
  Alert,
  MobileStepper
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import { storageService } from '../../services/storageService';

const STORAGE_KEY = 'visionBoards.json';
const MAX_STORAGE_SIZE = 10 * 1024 * 1024; // 10MB limit

function Vision() {
  const [visionBoards, setVisionBoards] = useState([]);
  const [newText, setNewText] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showFullscreenText, setShowFullscreenText] = useState(true);
  const [isInputTouched, setIsInputTouched] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);
  const [clickedText, setClickedText] = useState('');
  const [showClickAnimation, setShowClickAnimation] = useState(false);

  const defaultBoards = [
    {
      id: 0,
      text: "一切都係最好嘅安排!加油SPY💪",
      image: "./default-vision.jpg",
      isDefault: true
    },
    {
      id: 1,
      text: "言出必行 🐔&🐷 Support!",
      image: "./default-vision-2.jpg",
      isDefault: true
    },
    {
      id: 2,
      text: "Attention Go-Energy Flow-Result Show🌟",
      image: "./default-vision-3.jpg",
      isDefault: true
    }
  ];

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  const handleCardClick = (text) => {
    setClickedText(text);
    setShowClickAnimation(true);
    setTimeout(() => {
      setShowClickAnimation(false);
    }, 5000);
  };

  // 檢查並創建存儲目錄
  const initializeStorage = async () => {
    await storageService.initialize();
    if (Capacitor.isNativePlatform()) {
      try {
        await Filesystem.mkdir({
          path: 'visionboards',
          directory: Directory.Data,
          recursive: true
        });
      } catch (error) {
        if (error.message !== 'Directory exists') {
          console.error('Error creating directory:', error);
        }
      }
    }
  };

  // 保存圖片到文件系統
  const saveImage = async (imageData, filename) => {
    if (!Capacitor.isNativePlatform()) return imageData;

    try {
      const base64Data = imageData.split(',')[1];
      await Filesystem.writeFile({
        path: `visionboards/${filename}`,
        data: base64Data,
        directory: Directory.Data,
        recursive: true
      });
      
      // 返回完整的文件路徑
      const fullPath = `visionboards/${filename}`;
      return fullPath;
    } catch (error) {
      console.error('Error saving image:', error);
      throw error;
    }
  };

  // 從文件系統讀取圖片
  const loadImage = async (uri) => {
    if (!Capacitor.isNativePlatform()) return uri;

    try {
      // 如果是完整的 URI，直接使用
      if (uri.startsWith('data:image')) {
        return uri;
      }

      // 讀取文件
      const file = await Filesystem.readFile({
        path: uri,
        directory: Directory.Data
      });

      // 返回 base64 格式的圖片數據
      return `data:image/jpeg;base64,${file.data}`;
    } catch (error) {
      console.error('Error loading image:', error);
      if (error.message.includes('does not exist')) {
        // 如果文件不存在，返回默認圖片
        return defaultBoards[0].image;
      }
      return uri;
    }
  };

  // 刪除圖片文件
  const deleteImage = async (uri) => {
    if (!Capacitor.isNativePlatform() || !uri || uri === defaultBoards[0].image || uri.startsWith('data:image')) return;

    try {
      await Filesystem.deleteFile({
        path: uri,
        directory: Directory.Data
      });
    } catch (error) {
      console.error('Error deleting image file:', error);
    }
  };

  // 清理遺留的無用圖片 (孤兒檔案)
  const cleanupOrphanedImages = async (boards) => {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const vbDir = await Filesystem.readdir({
        path: 'visionboards',
        directory: Directory.Data
      });
      
      if (vbDir && vbDir.files) {
        const validPaths = new Set(
          boards
            .filter(b => !b.isDefault)
            .map(b => b.image)
            .filter(path => typeof path === 'string' && path.startsWith('visionboards/'))
            .map(path => path.split('/').pop())
        );
        
        for (const fileInfo of vbDir.files) {
          const fileName = fileInfo.name || fileInfo;
          if (!validPaths.has(fileName)) {
            console.log(`🧹 刪除遺留的願景板圖片: ${fileName}`);
            await Filesystem.deleteFile({
              path: `visionboards/${fileName}`,
              directory: Directory.Data
            });
          }
        }
      }
    } catch (error) {
      if (!error.message.includes('does not exist')) {
        console.error('清理遺留圖片失敗:', error);
      }
    }
  };

  // 保存數據
  const saveData = async (boards) => {
    try {
      const boardsToSave = boards.map(board => {
        if (board.isDefault) return board;
        return {
          ...board,
          image: board.imagePath || board.image, // 確保保存的是路徑而非 Base64
          displayImage: undefined, // 移除不必要的暫存 Base64
          imagePath: undefined
        };
      });
      const data = JSON.stringify(boardsToSave);
      await storageService.setItem(STORAGE_KEY, data);
      return true;
    } catch (error) {
      console.error('Error saving data:', error);
      showSnackbar('保存數據時出現錯誤', 'error');
      return false;
    }
  };

  // 加載數據
  const loadData = async () => {
    try {
      let data = await storageService.getItem(STORAGE_KEY);
      let parsedBoards = data ? JSON.parse(data) : [];
      let needsResave = false;

      if (parsedBoards.length > 0) {
        // 1. 修復歷史遺留的 Base64 數據 (若有)
        if (Capacitor.isNativePlatform()) {
          for (let board of parsedBoards) {
            if (!board.isDefault && board.image && board.image.startsWith('data:image')) {
              try {
                console.log(`修復願景板 ${board.id}，將 Base64 轉回文件`);
                const filename = `vision_${board.id}_recovered.jpg`;
                const savedImagePath = await saveImage(board.image, filename);
                board.image = savedImagePath; // 更新為文件路徑
                needsResave = true;
              } catch (e) {
                console.error('修復 Base64 圖片失敗:', e);
              }
            }
          }
          if (needsResave) {
            await storageService.setItem(STORAGE_KEY, JSON.stringify(parsedBoards));
          }
        }

        // 2. 清理無用的遺留圖片
        await cleanupOrphanedImages(parsedBoards);

        // 3. 加載所有圖片
        const loadedBoards = await Promise.all(
          parsedBoards.filter(board => !board.isDefault).map(async (board) => {
            try {
              const imageData = await loadImage(board.image);
              return {
                ...board,
                imagePath: board.image, // 保存原始路徑 (例如 visionboards/xxx.jpg)
                displayImage: imageData // 使用 displayImage 來綁定 UI
              };
            } catch (error) {
              console.error('Error loading image for board:', error);
              return {
                ...board,
                imagePath: board.image,
                displayImage: defaultBoards[0].image // 使用默認圖片作為後備
              };
            }
          })
        );
        setVisionBoards([...defaultBoards, ...loadedBoards]);
      } else {
        setVisionBoards(defaultBoards);
      }
      setTimeout(() => setIsInitialLoad(false), 100);
    } catch (error) {
      console.error('Error loading data:', error);
      setVisionBoards(defaultBoards);
      setTimeout(() => setIsInitialLoad(false), 100);
    }
  };

  // 初始化和加載數據
  useEffect(() => {
    const initialize = async () => {
      await initializeStorage();
      await loadData();
    };
    initialize();
  }, []);

  useEffect(() => {
    if (showFullscreenText) {
      // 等待 5 秒後隱藏全屏文字
      setTimeout(() => {
        setShowFullscreenText(false);
        // 文字動畫結束後啟用自動輪播
        setAutoPlayEnabled(true);
      }, 5000);
    }
  }, []);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('圖片大小不能超過 5MB', 'warning');
        return;
      }

      try {
        // 壓縮圖片
        const compressImage = (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
              const img = new Image();
              img.src = event.target.result;
              img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 1200;
                const MAX_HEIGHT = 1200;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                  if (width > MAX_WIDTH) {
                    height *= MAX_WIDTH / width;
                    width = MAX_WIDTH;
                  }
                } else {
                  if (height > MAX_HEIGHT) {
                    width *= MAX_HEIGHT / height;
                    height = MAX_HEIGHT;
                  }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                // 壓縮為 JPEG，品質 0.7
                resolve(canvas.toDataURL('image/jpeg', 0.7));
              };
              img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
          });
        };

        const compressedImageData = await compressImage(file);
        
        try {
          const filename = `vision_${Date.now()}.jpg`;
          const savedImagePath = await saveImage(compressedImageData, filename);
          
          const newBoard = {
            id: Date.now(),
            text: newText,
            image: savedImagePath,
            imagePath: savedImagePath,
            displayImage: compressedImageData,
            createdAt: new Date().toISOString()
          };

          const updatedBoards = visionBoards.filter(board => !board.isDefault);
          const finalBoards = [...defaultBoards, ...updatedBoards, newBoard];
          
          if (await saveData(finalBoards)) {
            // 重新加載數據以確保圖片正確顯示，或直接使用 state
            setVisionBoards(finalBoards);
            setNewText('');
            setIsInputTouched(false);
            showSnackbar('願景板已保存', 'success');
          }
        } catch (error) {
          console.error('Error processing image:', error);
          showSnackbar('保存圖片時出現錯誤', 'error');
        }
      } catch (error) {
        console.error('Error compressing file:', error);
        showSnackbar('處理圖片時出現錯誤', 'error');
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      const boardToDelete = visionBoards.find(board => board.id === id);
      if (boardToDelete) {
        // 先刪除圖片文件 (如果存在的話)
        await deleteImage(boardToDelete.imagePath || boardToDelete.image);
      }

      const updatedBoards = visionBoards.filter(board => board.id !== id);
      if (await saveData(updatedBoards)) {
        setVisionBoards(updatedBoards);
        showSnackbar('願景板已刪除', 'success');
      }
    } catch (error) {
      showSnackbar('刪除願景板時出現錯誤', 'error');
    }
  };

  return (
    <Box sx={{ 
      p: { xs: 0, sm: 2 },  // 減少移動端的外邊距
      pb: { xs: 6, sm: 2 }, // 減少移動端底部空間
      pt: { xs: 0, sm: 2 }
    }}>
      {showFullscreenText && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(253, 242, 248, 0.95)',
            zIndex: 9999,
            animation: 'fadeOut 1s ease-in-out 4s forwards',
            // 確保在移動端正確顯示
            padding: { xs: '0 16px', sm: 0 },
            '@keyframes fadeOut': {
              to: {
                opacity: 0,
                visibility: 'hidden'
              }
            }
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: 'primary.main',
              padding: '20px',
              fontSize: { xs: '2rem', sm: '3.75rem' },
              animation: 'pulseAndShrink 5s ease-in-out',
              '@keyframes pulseAndShrink': {
                '0%': {
                  transform: 'scale(0.5)',
                  color: 'primary.main'
                },
                '10%': {
                  transform: 'scale(1.2)',
                  color: 'primary.main'
                },
                '20%': {
                  transform: 'scale(1)',
                  color: 'primary.main'
                },
                '50%': {
                  transform: 'scale(1)',
                  color: 'primary.main'
                },
                '90%': {
                  transform: 'scale(0.6)',
                  color: 'primary.main'
                },
                '100%': {
                  transform: 'scale(0.3)',
                  color: '#ffffff'
                }
              }
            }}
          >
            {defaultBoards[activeStep].text}
          </Typography>
        </Box>
      )}

      {showClickAnimation && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(253, 242, 248, 0.95)',
            zIndex: 9999,
            animation: 'fadeOut 1s ease-in-out 4s forwards',
            padding: { xs: '0 16px', sm: 0 },
            '@keyframes fadeOut': {
              to: {
                opacity: 0,
                visibility: 'hidden'
              }
            }
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: 'primary.main',
              padding: '20px',
              fontSize: { xs: '2rem', sm: '3.75rem' },
              animation: 'pulseAndShrink 5s ease-in-out',
              '@keyframes pulseAndShrink': {
                '0%': {
                  transform: 'scale(0.5)',
                  color: 'primary.main'
                },
                '10%': {
                  transform: 'scale(1.2)',
                  color: 'primary.main'
                },
                '20%': {
                  transform: 'scale(1)',
                  color: 'primary.main'
                },
                '50%': {
                  transform: 'scale(1)',
                  color: 'primary.main'
                },
                '90%': {
                  transform: 'scale(0.6)',
                  color: 'primary.main'
                },
                '100%': {
                  transform: 'scale(0.3)',
                  color: '#ffffff'
                }
              }
            }}
          >
            {clickedText}
          </Typography>
        </Box>
      )}

      <Stack spacing={{ xs: 2, sm: 3 }}>
        {/* 默認願景板輪播 */}
        <Box sx={{ position: 'relative' }}>
          {/* 左箭頭 */}
          <IconButton
            className="swiper-button-prev-custom"
            sx={{
              position: 'absolute',
              left: { xs: -16, sm: -28 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
              },
              display: { xs: 'none', sm: 'flex' }, // 只在桌面顯示
              width: 40,
              height: 40,
            }}
          >
            <KeyboardArrowLeft />
          </IconButton>

          {/* 右箭頭 */}
          <IconButton
            className="swiper-button-next-custom"
            sx={{
              position: 'absolute',
              right: { xs: -16, sm: -28 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
              },
              display: { xs: 'none', sm: 'flex' }, // 只在桌面顯示
              width: 40,
              height: 40,
            }}
          >
            <KeyboardArrowRight />
          </IconButton>

          <Box
            sx={{ 
              width: '100%',
              '& .swiper': {
                paddingBottom: '50px'
              },
              '& .swiper-slide': {
                padding: '0 2px'
              },
              '& .swiper-pagination': {
                bottom: '10px'
              },
              '& .swiper-pagination-bullet': {
                width: '8px',
                height: '8px',
                backgroundColor: 'rgba(255, 192, 203, 0.3)',
                opacity: 1
              },
              '& .swiper-pagination-bullet-active': {
                width: '24px',
                backgroundColor: 'primary.main',
                borderRadius: '4px'
              }
            }}
          >
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={8}
              slidesPerView={1}
              navigation={{
                prevEl: '.swiper-button-prev-custom',
                nextEl: '.swiper-button-next-custom'
              }}
              pagination={{
                clickable: true,
                dynamicBullets: false
              }}
              autoplay={autoPlayEnabled ? {
                delay: 5000,
                disableOnInteraction: false
              } : false}
              speed={600}
              grabCursor={true}
              onSlideChange={(swiper) => handleStepChange(swiper.activeIndex)}
              onSwiper={(swiper) => {
                // Store swiper instance if needed
                swiper.slideTo(activeStep, 0);
              }}
              style={{
                width: '100%',
                height: 'auto'
              }}
            >
              {defaultBoards.map((board) => (
                <SwiperSlide key={board.id}>
                  <Card
                    onClick={() => handleCardClick(board.text)}
                    sx={{
                      borderRadius: { xs: '16px', sm: '20px' },
                      overflow: 'hidden',
                      boxShadow: 'none',
                      background: 'transparent',
                      position: 'relative',
                      cursor: 'pointer',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '10%',
                        width: '80%',
                        height: '40px',
                        background: 'rgba(0, 0, 0, 0.1)',
                        filter: 'blur(20px)',
                        borderRadius: '50%',
                        zIndex: -1
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', borderRadius: 'inherit', overflow: 'hidden' }}>
                      <CardMedia
                        component="img"
                        image={board.displayImage || board.image}
                        alt="Vision Board"
                        sx={{
                          height: { xs: 300, sm: 400 },
                          width: '100%',
                          maxWidth: '100%',
                          transform: 'scale(1)',
                          transition: 'transform 0.6s ease',
                          objectFit: 'cover',
                          objectPosition: 'center bottom'
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 20%, transparent 100%)',
                          backdropFilter: 'blur(6px)',
                          padding: { xs: '4px 8px', sm: '6px 12px' },
                          color: 'white',
                          transform: 'translateY(0)',
                          transition: 'all 0.3s ease',
                          maxHeight: '20%',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          '&:hover': {
                            background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 30%, transparent 100%)',
                            backdropFilter: 'blur(6px)',
                          }
                        }}
                      >
                        <Typography
                          variant="h4"
                          sx={{
                            textAlign: 'center',
                            fontWeight: '600',
                            letterSpacing: '0.5px',
                            fontSize: { xs: '1.35rem', sm: '1.75rem' },
                            textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                            transform: 'translateY(0)',
                            transition: 'transform 0.3s ease',
                            color: 'white',
                            lineHeight: 1.2,
                            mb: 0.25
                          }}
                        >
                          {board.text}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>


          </Box>
        </Box>

        {/* 用戶的願景板 */}
        {visionBoards.filter(board => !board.isDefault).map((board) => (
          <Card 
            key={board.id} 
            sx={{ 
              position: 'relative',
              borderRadius: { xs: '12px', sm: '16px' }, // 減少移動端圓角
              overflow: 'hidden',
              boxShadow: { 
                xs: '0 4px 12px rgba(0,0,0,0.08)', 
                sm: '0 8px 24px rgba(0,0,0,0.12)' 
              },
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:active': {
                transform: 'scale(0.98)'
              },
              '@media (hover: hover)': {
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.18)'
                }
              }
            }}
          >
            <CardMedia
              component="img"
              image={board.displayImage || board.image}
              alt="Vision Board"
              sx={{
                height: { xs: 280, sm: 400 },
                filter: 'brightness(1)'
              }}
            />
            
            <Box
              sx={{
                position: 'relative',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                padding: { xs: '8px', sm: '12px' }, // 減少內邊距
                borderTop: '1px solid rgba(200, 200, 200, 0.3)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '40%',
                  height: '2px',
                  background: 'linear-gradient(90deg, transparent, rgba(255, 192, 203, 0.7), transparent)'
                }
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  textAlign: 'center',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                  position: 'relative',
                  padding: { xs: '0 8px', sm: '0 12px' }, // 減少文字內邊距
                  fontSize: { xs: '1.125rem', sm: '1.5rem' },
                  color: 'primary.main',
                  animation: 'fadeIn 0.5s ease-out',
                  '@keyframes fadeIn': {
                    from: {
                      opacity: 0,
                      transform: 'translateY(10px)'
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)'
                    }
                  }
                }}
              >
                {board.text}
              </Typography>
            </Box>
            
            {!board.isDefault && (
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 8,  // 調整刪除按鈕位置
                  right: 8,
                  color: 'rgba(255,255,255,0.6)',
                  backgroundColor: 'transparent',
                  opacity: 0.4,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    opacity: 0.8
                  },
                  transition: 'all 0.2s ease-in-out',
                  width: 28,  // 稍微縮小按鈕大小
                  height: 28,
                  transform: 'scale(0.9)'
                }}
                onClick={() => handleDelete(board.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Card>
        ))}
      </Stack>

      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 3 },  // 減少移動端的內邊距
          mt: { xs: 3, sm: 4 },  // 減少移動端的上邊距
          borderRadius: { xs: '12px', sm: '16px' },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
            Add VisionBoard
          </Typography>
          
          <TextField
            required
            fullWidth
            label="Movitate Yourself"
            placeholder="Input motivational words"
            value={newText}
            onChange={(e) => {
              setNewText(e.target.value);
              setIsInputTouched(true);
            }}
            onBlur={() => setIsInputTouched(true)}
            multiline
            rows={2}
            error={isInputTouched && !newText}
            helperText={isInputTouched && !newText ? "Input motivational words" : ""}
            inputProps={{
              lang: 'zh-HK',
              spellCheck: 'false',
              autoComplete: 'off'
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main'
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.main'
              }
            }}
          />
          
          <Button
            variant="contained"
            component="label"
            startIcon={<AddPhotoAlternateIcon />}
            disabled={!newText}
            sx={{ 
              mt: 2,
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            Upload VisionBoard Image
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageUpload}
            />
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Vision;